import { useEffect, useState } from "react";
import { getAdminUploadJob, type AdminUploadJobDetail } from "@/api/adminUploadJob";
import { getAdminDiagnosticJob, type AdminDiagnosticJobDetail } from "@/api/adminDiagnosticJob";
import { adminOperationError } from "@/app/adminOperationError";

export type WorkerJobDetail =
  | { kind: "upload"; job: AdminUploadJobDetail }
  | { kind: "diagnostic"; job: AdminDiagnosticJobDetail };

export function useWorkerJobDetail(kind: WorkerJobDetail["kind"], uuid: string, active = true) {
  const [detail, setDetail] = useState<WorkerJobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let loading = false;
    let timer: ReturnType<typeof setTimeout>;
    setDetail(null);
    setError(null);
    const isVisible = () => document.visibilityState !== "hidden";
    const load = async () => {
      if (cancelled || loading || !isVisible()) return;
      clearTimeout(timer);
      loading = true;
      try {
        const next: WorkerJobDetail =
          kind === "upload"
            ? { kind, job: await getAdminUploadJob(uuid) }
            : { kind, job: await getAdminDiagnosticJob(uuid) };
        if (!cancelled) {
          setDetail(next);
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) {
          setDetail(null);
          setError(adminOperationError(caught));
        }
      } finally {
        loading = false;
        // 보이는 화면에서 직전 응답을 받은 뒤 다음 폴링을 예약한다.
        if (!cancelled && isVisible())
          timer = setTimeout(() => {
            void load();
          }, 5_000);
      }
    };
    const onVisibilityChange = () => {
      clearTimeout(timer);
      void load();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    void load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [kind, uuid, revision, active]);
  return { detail, error, refresh: () => setRevision((value) => value + 1) };
}
